import {Link, MatchRenderProps, navigate} from '@reach/router';
import React, {ReactNode, useCallback, useContext, useEffect, useRef} from 'react';
import {RouteStringValues, SIMPLE_ROUTES, SimpleRouteKeys} from 'routing/route';
import SidebarMenuContext from './utils/sidebarMenuContext';
import {useStoreActions, useStoreState} from 'store';
import {LinkActivityMatcher} from 'components/sidebar/utils/LinkActivityMatcher';
import {MatchResult} from 'routing/utils/types';


type RouteProp = {
    // this is liberal string because it can be a full URL (with params)
    route: string;
    routeName?: never;
} | {
    route?: never;
    routeName: SimpleRouteKeys;
}

interface OtherProps {
    isActive?: (match: MatchResult) => boolean;
    activeRoutes?: RouteStringValues[];
    forceReload?: boolean;
    children?: ReactNode;
}

type Props = RouteProp & OtherProps;

const isActiveDefaultCalc = (match: MatchRenderProps<{}>) => !!match;

const SidebarMenuLink = React.memo<Props>((
    {
        route,
        routeName,
        isActive = isActiveDefaultCalc,
        activeRoutes = [],
        forceReload = false,
        children,
    },
) => {
    const appConfig = useStoreState((state) => state.appConfigs.items);
    const sidebarMenuSectionName = useContext(SidebarMenuContext);

    const linkRoute = route ?? SIMPLE_ROUTES[routeName];

    const setOpenSection = useStoreActions((actions) => actions.sidebarMenu.setOpenSection);

    const activeLinkRoute = useStoreState((state) => state.sidebarMenu.activeLinkRoute);
    const setActiveLinkRoute = useStoreActions((actions) => actions.sidebarMenu.setActiveLinkRoute);

    const linkIsActive = activeLinkRoute === linkRoute || 
        activeRoutes.some(route => activeLinkRoute.startsWith(route));
    
    const activeLinkRouteRef = useRef<string | null>(null);

    useEffect(() => {
        if (linkIsActive) {
            setOpenSection(sidebarMenuSectionName);
        }
    }, [
        setOpenSection,
        linkIsActive,
        sidebarMenuSectionName,
    ]);

    useEffect(() => {
        if (activeLinkRouteRef.current) {
            setActiveLinkRoute(activeLinkRouteRef.current);
            activeLinkRouteRef.current = null;
        }
    }, [setActiveLinkRoute]);

    const onLinkMatch = useCallback(({match}: {match: MatchResult}) => {
        if (!match) {return null;}
        
        const computedIsActive = isActive(match);

        if (computedIsActive) {
            activeLinkRouteRef.current = linkRoute;
        }

        return null;
    }, [isActive, linkRoute]);

    const matchingRoutes = [...activeRoutes];
    if (matchingRoutes.length === 0) {
        matchingRoutes.push(linkRoute);
    }

    const linkMatchers = matchingRoutes.map((route, index) => (
        <LinkActivityMatcher
            key={`${linkRoute}-matcher-${index}`}
            route={route}
            onLinkMatch={onLinkMatch}
        />
    ));

    const handleClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        setActiveLinkRoute(linkRoute);
        
        if (window.innerWidth < 992) {
            document.body.classList.remove('show-sidebar');
        }
        
        if (appConfig.FLASKPAGE || forceReload) {
            navigate(linkRoute);
            e.stopPropagation();
        }
    };

    return (
        <li data-cy="sidebar-menu-link">
            {linkMatchers}
            <Link
                to={linkRoute}
                className={`nav-link text-decoration-none ${linkIsActive ? 'active' : ''}`}
                onClick={handleClick}
                data-cy="sidebar-nav-link"
            >
                {children}
            </Link>
        </li>
    );
}, (prevProps: Props, nextProps: Props): boolean => {
    let allAreEqual = true;

    // Specific comparison for activeRoutes because they are static but JS can't tell that they haven't changed
    Object.keys(prevProps).forEach((key) => {
        if (key === 'activeRoutes' && Array.isArray(prevProps.activeRoutes) && Array.isArray(nextProps.activeRoutes)) {
            prevProps.activeRoutes.forEach((route: RouteStringValues, idx: number) => {
                if (nextProps.activeRoutes && route !== nextProps.activeRoutes[idx]) {
                    allAreEqual = false;
                }
            });
            return;
        }

        if (prevProps[key as keyof Props] !== nextProps[key as keyof Props]) {
            allAreEqual = false;
        }
    });

    return allAreEqual;
});


export default SidebarMenuLink;
